/**
 * StylePresetPicker — Phase 2 component for the Create page.
 *
 * Renders a card grid of artist-inspired presets grouped by category. When
 * a user clicks "Use This Style", the parent applies the preset via
 * onApply(preset). Engine-aware behavior happens in the parent — this
 * component just surfaces the data.
 *
 * Public GET /api/style-presets returns enabled presets sorted by
 * display_order, with engine-specific payloads stripped (they live
 * server-side only).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Music, Pause, Play, Search, Sparkles, X } from 'lucide-react';
import { CoachEye } from './Coach';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level singleton so only ONE preset preview plays at a time —
// hovering a different card silently swaps the source out from under
// the previous one.
let __currentPresetAudio = null;

export default function StylePresetPicker({ isOpen, onClose, onApply, engineLabel }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    if (!isOpen || presets.length > 0) return;
    setLoading(true);
    fetch(`${API}/style-presets`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPresets(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.debug('Failed to load style presets:', e);
        setPresets([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, presets.length]);

  const categories = useMemo(() => {
    const set = new Set(presets.map((p) => p.category));
    return ['all', ...Array.from(set)];
  }, [presets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return presets.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (!q) return true;
      return (
        (p.artist_name || '').toLowerCase().includes(q) ||
        (p.style_tags || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    });
  }, [presets, query, category]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="style-preset-picker-modal"
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <span>Style Inspired By…</span>
              <CoachEye targetId="create.style_inspired_by.howto" label="How to use Style Inspired By" />
            </h2>
            <p className="text-sm text-zinc-400 mt-1">
              Pick a musical influence — we&apos;ll set the style tags for you.
              {engineLabel === 'MUSICGPT' && (
                <> Your prompt will also note the inspiration so MusicGPT leans into it.</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1"
            data-testid="style-preset-picker-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search + categories */}
        <div className="p-4 border-b border-white/10 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by artist, genre, or style tag…"
              className="input-dark w-full pl-9 py-2"
              data-testid="style-preset-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                  category === c
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
                data-testid={`style-preset-category-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
        </div>

        {/* Card grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="text-center py-12 text-zinc-500" data-testid="style-preset-loading">
              Loading inspirations…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              No presets match your search.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <PresetCard
                  key={p.preset_id}
                  preset={p}
                  onApply={() => {
                    onApply(p);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Disclaimer footer */}
        <div className="p-4 border-t border-white/10 bg-black/40">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Style presets inspired by various musical influences.
            These presets add genre, instrument & production style tags. All vocals are AI-generated
            and are not performed by the named artists. WrappedInMusic does not claim affiliation
            with any artist listed.
          </p>
        </div>
      </div>
    </div>
  );
}

function PresetCard({ preset, onApply }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  // Prefer the trimmed 12s preview; fall back to the full track so admins
  // can audition before they trim. Falsy when no audio uploaded yet.
  const previewSrc = preset.preview_audio_url || preset.full_audio_url || null;

  // Stop playback whenever the card unmounts (e.g. modal closes) so we
  // don't leak a hidden audio element playing in the background.
  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el) {
        try { el.pause(); } catch { /* noop */ }
      }
      if (__currentPresetAudio === audioRef.current) {
        __currentPresetAudio = null;
      }
    };
  }, []);

  const startPreview = () => {
    if (!previewSrc) return;
    // Pause whoever was playing before this card
    if (__currentPresetAudio && __currentPresetAudio !== audioRef.current) {
      try { __currentPresetAudio.pause(); } catch { /* noop */ }
    }
    const el = audioRef.current;
    if (!el) return;
    __currentPresetAudio = el;
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      setPlaying(true);
    }
  };

  const stopPreview = () => {
    const el = audioRef.current;
    if (el) {
      try { el.pause(); } catch { /* noop */ }
    }
    setPlaying(false);
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-lg p-3 hover:border-amber-500/40 hover:bg-white/[0.05] transition-all flex flex-col"
      data-testid={`style-preset-card-${preset.preset_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm truncate flex items-center gap-1.5">
            {/* Hover (or tap on mobile) the icon to preview the style.
                Falls back to a plain Music icon when no audio uploaded. */}
            <button
              type="button"
              onMouseEnter={startPreview}
              onMouseLeave={stopPreview}
              onFocus={startPreview}
              onBlur={stopPreview}
              onClick={(e) => {
                // Toggle on click for touch devices that don't fire hover.
                e.stopPropagation();
                if (!previewSrc) return;
                if (playing) stopPreview(); else startPreview();
              }}
              disabled={!previewSrc}
              className={`flex-shrink-0 rounded-full p-0.5 transition-all ${
                previewSrc
                  ? 'text-amber-400 hover:text-amber-300 hover:scale-110 cursor-pointer'
                  : 'text-amber-400/40 cursor-default'
              } ${playing ? 'animate-pulse scale-110' : ''}`}
              aria-label={previewSrc ? `Preview ${preset.artist_name} style` : 'No preview available'}
              data-testid={`style-preset-preview-btn-${preset.preset_id}`}
              title={previewSrc ? 'Hover or click to preview' : 'Preview not uploaded yet'}
            >
              {playing ? (
                <Pause className="h-3.5 w-3.5" />
              ) : previewSrc ? (
                <Play className="h-3.5 w-3.5" />
              ) : (
                <Music className="h-3.5 w-3.5" />
              )}
            </button>
            {preset.artist_name}
          </h3>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            {preset.era} · {preset.category}
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-400 mb-3 leading-snug line-clamp-3 flex-1">
        {preset.style_tags}
      </p>
      <button
        type="button"
        onClick={onApply}
        className="w-full text-xs bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 hover:border-amber-500/50 py-1.5 rounded-md transition-all font-medium"
        data-testid={`style-preset-apply-${preset.preset_id}`}
      >
        Use This Style
      </button>
      {previewSrc && (
        <audio
          ref={audioRef}
          src={previewSrc}
          preload="none"
          onEnded={() => {
            setPlaying(false);
            if (__currentPresetAudio === audioRef.current) {
              __currentPresetAudio = null;
            }
          }}
          onPause={() => setPlaying(false)}
        />
      )}
    </div>
  );
}
